import DOMPurify from 'isomorphic-dompurify';

/**
 * Types and interfaces for the sanitization utilities
 */
export type SanitizationResult = {
  valid: boolean;
  clean: string;
  reason?: string;
};

export type FieldSanitizationResult = SanitizationResult;

export type MultiFieldSanitizationResult<T> = {
  allValid: boolean;
  results: Record<keyof T, SanitizationResult>;
};

export interface SanitizeOptions {
  /** Ratio determining how much the string can be shortened before considering it harmful */
  strictnessRatio?: number;
  /** Whether to allow any HTML tags (default: false) */
  allowHtml?: boolean;
  /** Specific HTML tags to allow if allowHtml is true */
  allowedTags?: string[];
  /** Specific HTML attributes to allow if allowHtml is true */
  allowedAttributes?: string[];
  /** Whether to encode HTML special characters */
  encodeEntities?: boolean;
}

/**
 * Default sanitization options
 */
const DEFAULT_SANITIZE_OPTIONS: SanitizeOptions = {
  strictnessRatio: 0.5,
  allowHtml: false,
  allowedTags: [],
  allowedAttributes: [],
  encodeEntities: true,
};

/**
 * Pattern to detect potentially harmful script content
 */
const HARMFUL_PATTERNS = {
  scriptTags: /<script.*?>|on\w+=".*?"|javascript:/gi,
  htmlTags: /<[^>]*>?/g,
  specialChars: /[<>&"']/g,
};

/**
 * Map of special characters to their HTML entity equivalents
 */
const HTML_ENTITY_MAP: Record<string, string> = {
  '<': '&lt;',
  '>': '&gt;',
  '&': '&amp;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * Sanitizes a single string input to prevent XSS attacks
 *
 * @param input - The string to sanitize
 * @param options - Sanitization options
 * @returns A sanitization result object containing the sanitized string and validation info
 */
export function sanitizeInput(
  input: string,
  options: SanitizeOptions = DEFAULT_SANITIZE_OPTIONS
): SanitizationResult {
  const opts = { ...DEFAULT_SANITIZE_OPTIONS, ...options };

  // Handle empty input
  if (!input) {
    return { valid: true, clean: '' };
  }

  // Check for harmful patterns
  if (HARMFUL_PATTERNS.scriptTags.test(input)) {
    return {
      valid: false,
      clean: '',
      reason: 'Contains script tags or inline JS handlers',
    };
  }

  // First strip all HTML tags with a regex for extra security
  let cleaned = input;
  if (!opts.allowHtml) {
    cleaned = input.replace(HARMFUL_PATTERNS.htmlTags, '');
  }

  // Use DOMPurify for thorough sanitization
  const purifyConfig = {
    ALLOWED_TAGS: opts.allowHtml ? opts.allowedTags : [],
    ALLOWED_ATTR: opts.allowHtml ? opts.allowedAttributes : [],
    FORBID_TAGS: [
      'style',
      'script',
      'iframe',
      'form',
      'object',
      'embed',
      'link',
      'meta',
    ],
    FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick', 'onmouseover'],
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  };

  const purified = DOMPurify.sanitize(cleaned, purifyConfig);

  // Additional encoding for special characters if requested
  let result = purified;
  if (opts.encodeEntities) {
    result = purified.replace(
      HARMFUL_PATTERNS.specialChars,
      (match) => HTML_ENTITY_MAP[match] || match
    );
  }

  // Check if the sanitization removed too much content (possible harmful input)
  if (result.length < input.length * (opts.strictnessRatio || 0.5)) {
    return {
      valid: false,
      clean: result,
      reason: 'Sanitized input is too short, likely harmful',
    };
  }

  return { valid: true, clean: result };
}

/**
 * Sanitizes HTML content for safe display with more permissive settings
 *
 * @param html - The HTML content to sanitize
 * @param allowedTags - List of allowed HTML tags
 * @param allowedAttributes - List of allowed HTML attributes
 * @returns Sanitized HTML string
 */
export function sanitizeHtml(
  html: string,
  allowedTags: string[] = [
    'p',
    'b',
    'i',
    'em',
    'strong',
    'a',
    'ul',
    'ol',
    'li',
  ],
  allowedAttributes: string[] = ['href', 'target']
): string {
  if (!html) return '';

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: allowedAttributes,
    FORBID_TAGS: ['script', 'style', 'iframe', 'form', 'object'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
  });
}

/**
 * Encodes special HTML characters to prevent rendering as HTML
 *
 * @param text - Text to encode
 * @returns Text with HTML entities encoded
 */
export function encodeHtmlEntities(text: string): string {
  if (!text) return '';

  return text.replace(
    HARMFUL_PATTERNS.specialChars,
    (match) => HTML_ENTITY_MAP[match] || match
  );
}

/**
 * Sanitizes an object with multiple string fields
 *
 * @param fields - Object with string fields to sanitize
 * @param options - Sanitization options
 * @returns Object with sanitization results for each field
 */
export function sanitizeMultiFieldInput<T extends Record<string, string>>(
  fields: T,
  options: SanitizeOptions = DEFAULT_SANITIZE_OPTIONS
): MultiFieldSanitizationResult<T> {
  const results: Record<string, SanitizationResult> = {};
  let allValid = true;

  for (const [key, value] of Object.entries(fields)) {
    const result = sanitizeInput(value, options);
    results[key] = result;

    if (!result.valid) {
      allValid = false;
    }
  }

  return {
    allValid,
    results: results as Record<keyof T, SanitizationResult>,
  };
}

/**
 * Sanitizes a filename to prevent path traversal and command injection
 *
 * @param filename - The filename to sanitize
 * @returns Sanitized filename
 */
export function sanitizeFilename(filename: string): string {
  if (!filename) return '';

  // Remove path traversal sequences and control characters
  const clean = filename
    .replace(/\.\.\//g, '') // Remove path traversal
    .replace(/[/\\?%*:|"<>]/g, '_') // Replace unsafe filename characters
    // eslint-disable-next-line no-control-regex
    .replace(/[\0-\x1F\x7F-\x9F]/g, ''); // Remove control characters

  // Encode any remaining HTML special chars
  return encodeHtmlEntities(clean);
}

/**
 * Sanitizes URL inputs to prevent javascript: protocol exploits and other malicious URLs
 *
 * @param url - The URL to sanitize
 * @returns Sanitized URL if safe, empty string if unsafe
 */
export function sanitizeUrl(url: string): string {
  if (!url) return '';

  // Simple protocol check
  const lowercaseUrl = url.toLowerCase().trim();
  if (
    lowercaseUrl.startsWith('javascript:') ||
    lowercaseUrl.startsWith('data:') ||
    lowercaseUrl.startsWith('vbscript:')
  ) {
    return '';
  }

  // Use DOMPurify's URL sanitizer
  const sanitized = DOMPurify.sanitize(url, { ALLOWED_TAGS: ['a'] });
  const tempElement = document.createElement('div');
  tempElement.innerHTML = sanitized;

  const anchor = tempElement.querySelector('a');
  return anchor ? anchor.href : '';
}

/**
 * Safely serialize object to JSON with HTML encoding
 *
 * @param data - The data to serialize
 * @returns Safe JSON string with HTML entities encoded
 */
export function safeJsonStringify(data: unknown): string {
  const jsonString = JSON.stringify(data);
  return encodeHtmlEntities(jsonString);
}
